/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { FileInput } from '@motili/ui';
import { styled } from 'styled-components';

import Config from 'Config';
import {
    assignFilePreview,
    assignRejectionPreview,
    generateRandomUUID,
} from 'common/utils/Utils';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import classNames from 'classnames';
import styles from './FileUploadDropzone.module.scss';
import { useUI } from '../../context/UIContext';
import LegacyFileUploadDropZone from './FileUploadDropzone-Legacy';

export function getNotApplicationFiles() {
    return [
        {
            preview: Config.notApplicablePhoto.url,
            name: generateRandomUUID(),
            type: Config.notApplicablePhoto.type,
        },
    ];
}

class FileUploadDropZone extends Component {
    render() {
        return (
            <>
                {this.props.readOnly ? (
                    <StyledDropzone>
                        <StyledContent>
                            <StyledCloudIcon className='fa fa-cloud-upload' />
                            <h4>{`Drag and Drop ${
                                this.props.multiple ? 'Files' : 'a File'
                            } Here`}</h4>
                            or click to select a file to upload
                        </StyledContent>
                    </StyledDropzone>
                ) : (
                    <StyledFileInput
                        id='dropzone'
                        accept={this.props.accept}
                        className={`${
                            !this.props.acls || this.props.disabled
                                ? ` ${styles.disabled}`
                                : ''
                        } ${this.props.className || ''}`}
                        disabled={!this.props.acls || this.props.disabled}
                        multiple={this.props.multiple}
                        onDrop={(acceptedFiles, rejectedFiles) => {
                            const _acceptedFiles =
                                acceptedFiles?.map(assignFilePreview);
                            const _rejectedFiles = rejectedFiles?.map(
                                assignRejectionPreview
                            );

                            this.props.handleOnDrop(
                                _acceptedFiles,
                                _rejectedFiles
                            );
                        }}
                        dataTestId={this.props.controlId}
                    />
                )}
                {this.props.enableNotApplicablePhoto && (
                    <div
                        className={classNames(
                            styles['not-applicable-container'],
                            {
                                [styles['text-center']]:
                                    this.props.centerNotApplicableText,
                            }
                        )}
                    >
                        <NotApplicableLink
                            disabled={!this.props.acls || this.props.disabled}
                            onClick={() => {
                                if (!(!this.props.acls || this.props.disabled))
                                    this.props.handleOnDrop(
                                        getNotApplicationFiles(),
                                        []
                                    );
                            }}
                        >
                            Click here{' '}
                        </NotApplicableLink>
                        <span>if Photo Requirement is Not Applicable</span>
                    </div>
                )}
            </>
        );
    }
}

FileUploadDropZone.defaultProps = {
    accept: {
        'image/jpeg': [],
        'image/png': [],
        'application/pdf': [],
    },
    disabled: false,
    multiple: true,
    acls: false,
    enableNotApplicablePhoto: false,
};

FileUploadDropZone.propTypes = {
    accept: PropTypes.shape({
        'image/jpeg': PropTypes.arrayOf(PropTypes.string),
        'image/png': PropTypes.arrayOf(PropTypes.string),
        'application/pdf': PropTypes.arrayOf(PropTypes.string),
    }),
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    acls: PropTypes.bool,
    handleOnDrop: PropTypes.func.isRequired,
    enableNotApplicablePhoto: PropTypes.bool,
    readOnly: PropTypes.bool,
    centerNotApplicableText: PropTypes.bool,
};

function FileUploadButton({
    accept = 'image/jpeg, image/png, application/pdf',
    disabled = false,
    multiple = true,
    acls = false,
    text,
    handleOnDrop,
}) {
    // Convert string accept to object format for react-dropzone v14
    const convertAcceptToObject = acceptProp => {
        if (typeof acceptProp === 'object') {
            return acceptProp;
        }

        const mimeTypes = acceptProp.split(',').map(type => type.trim());
        const acceptObject = {};
        mimeTypes.forEach(mimeType => {
            acceptObject[mimeType] = [];
        });
        return acceptObject;
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        accept: convertAcceptToObject(accept),
        onDrop: (acceptedFiles, fileRejections) => {
            const rejectedFiles = fileRejections.map(
                rejection => rejection.file
            );

            handleOnDrop(acceptedFiles, rejectedFiles);
        },
        disabled: !acls || disabled,
        multiple,
        noClick: true,
        noKeyboard: true,
    });

    return (
        <div>
            <div
                {...getRootProps({
                    style: { width: '0px', height: '0px', border: 'none' },
                })}
            >
                <input {...getInputProps()} id='upload-button' />
            </div>
            <MotiliButton
                text={text}
                moStyle='normal'
                disabled={disabled}
                onClick={e => {
                    e.stopPropagation();
                    open();
                }}
            />
        </div>
    );
}

FileUploadButton.propTypes = {
    accept: PropTypes.string,
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    acls: PropTypes.bool,
    text: PropTypes.string,
    handleOnDrop: PropTypes.func.isRequired,
};

const StyledDropzone = styled.div`
    width: 100%;
    height: 200px;
    border-width: 2px;
    border-color: #818181;
    border-style: dashed;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: no-drop;
    & h4 {
        font-size: 16px;
        font-weight: 700;
    }
    & a {
        font-size: 14px;
        font-weight: 600;
    }
`;

const StyledContent = styled.div`
    text-align: center;
    color: #818181;
`;

const StyledCloudIcon = styled.i`
    color: #818181;
    font-size: 75px;
`;

const NotApplicableLink = styled.em`
    font-size: 14px;
    font-weight: 700;
    color: ${({ disabled }) =>
        disabled ? Config.color.mediumGray : Config.color.motiliLightBlue};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

const StyledFileInput = styled(FileInput)`
    border-radius: 0;
    border: 2px dashed #cccccc;
    & h4 {
        font-size: 16px;
        font-weight: 700;
    }
    & a {
        font-size: 14px;
        font-weight: 600;
    }
`;

const FileUploadDropZoneWrapper = props => {
    const { isLegacyUI } = useUI();
    return isLegacyUI ? (
        <LegacyFileUploadDropZone {...props} />
    ) : (
        <FileUploadDropZone {...props} />
    );
};

export default FileUploadDropZoneWrapper;

export { FileUploadButton };
export { FileUploadDropZone };
